<template>
  <div class="portal-turnos-listado-seccion mt-3">
    <div v-if="loading" class="pa-3 pt-0">
      <v-row dense align="stretch">
        <v-col
          v-for="n in skeletonColCount"
          :key="`sk-${n}`"
          cols="12"
          sm="6"
          :md="colMd"
        >
          <v-skeleton-loader
            type="card"
            class="rounded-lg portal-turnos-listado-seccion__card-skeleton h-100"
          />
        </v-col>
      </v-row>
    </div>

    <v-row
      v-else-if="items.length === 0 || error"
      dense
      class="portal-turnos-listado-seccion__empty"
    >
      <v-col cols="12">
        <div
          class="d-flex align-center justify-center text-body-2 text-medium-emphasis py-8 px-2"
        >
          {{ emptyCopy }}
        </div>
      </v-col>
    </v-row>

    <v-row v-else dense align="start" class="pa-3 pt-0">
      <v-col
        v-for="(it, index) in items"
        :key="it.id"
        cols="12"
        sm="6"
        :md="colMd"
      >
        <portal-turno-paciente-card
          :item="it"
          :permite-cancelar="permiteCancelar"
          :preparacion-loading="preparacionTurnoId === it.turnoId"
          :display="cardDisplay"
          :destacada="destacarPrimerTurno && index === 0"
          :no-confirmar-automaticamente-turnos="noConfirmarAutomaticamenteTurnos"
          @ver-preparacion="onPedidoVerPreparacion(it)"
          @cancelar="onPedidoCancelar(it)"
        />
      </v-col>
    </v-row>

    <v-dialog
      v-model="preparacionDialogAbierto"
      max-width="720"
      scrollable
    >
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center py-3 pr-1 pl-4">
          <span class="text-h6 font-weight-bold text-primary min-w-0">
            Preparación del turno
          </span>
          <v-spacer />
          <v-tooltip location="start" text="Cerrar">
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                icon
                variant="text"
                color="primary"
                class="flex-shrink-0"
                aria-label="Cerrar"
                @click="preparacionDialogAbierto = false"
              >
                <v-icon :icon="preparacionDialogCloseIcon" size="large" />
              </v-btn>
            </template>
          </v-tooltip>
        </v-card-title>
        <v-card-text
          class="text-body-2 py-0 pb-4 portal-turnos-listado-seccion__preparacion-dialog-body"
        >
          <p
            v-if="resumenTurnoPreparacion"
            class="text-medium-emphasis mb-4 portal-turnos-listado-seccion__preparacion-text"
          >
            {{ resumenTurnoPreparacion }}
          </p>
          <v-divider v-if="resumenTurnoPreparacion" class="mb-4" />
          <v-card
            variant="outlined"
            rounded="lg"
            color="grey-lighten-4"
            class="portal-turnos-listado-seccion__preparacion-card"
          >
            <v-card-text class="text-body-2 py-4">
              <div
                v-if="preparacionEsHtml"
                class="text-medium-emphasis portal-turnos-listado-seccion__preparacion-html"
                v-html="preparacionTexto"
              />
              <p
                v-else
                class="text-medium-emphasis portal-turnos-listado-seccion__preparacion-text mb-0"
              >
                {{ preparacionTexto || 'Este turno no tiene preparación registrada.' }}
              </p>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm-dialog
      v-model="cancelDialogAbierto"
      title="Cancelar turno"
      :message="mensajeCancelarTurno"
      confirm-text="Sí, cancelar"
      cancel-text="Volver"
      confirm-color="error"
      :loading="cancelEnviando"
      @confirm="onConfirmCancelarTurno"
      @cancel="onCerrarDialogoCancelar"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog.vue'
import PortalTurnoPacienteCard from './PortalTurnoPacienteCard.vue'
import icons from '../../../utils/enums/icons'
import { preparacionDesdeRespuestaApi } from '../../../utils/helpers/asistentePreparacionTurno'

/** Mismas líneas de contexto que el diálogo de cancelar (sin el texto de confirmación). */
function lineasResumenTurnoPaciente(it) {
  if (!it) return []
  const lineas = []
  const especialidad = String(it.especialidad ?? '').trim()
  if (especialidad) lineas.push(`Especialidad: ${especialidad}`)
  const medico = String(it.medico ?? '').trim()
  if (medico) lineas.push(`Profesional: ${medico}`)
  const fecha = String(it.fechaDisplay ?? '').trim()
  const hora = String(it.horaDisplay ?? '').trim()
  if (fecha && hora) lineas.push(`Fecha: ${fecha} - ${hora}`)
  else if (fecha) lineas.push(`Fecha: ${fecha}`)
  else if (hora) lineas.push(`Hora: ${hora}`)
  if (it.esVideoconsulta) {
    lineas.push('Modalidad: VIDEOCONSULTA')
  } else {
    const lugar = String(it.lugar ?? '').trim()
    if (lugar) lineas.push(`Lugar: ${lugar}`)
  }
  const osPlan = String(it.osPlanDisplay ?? '').trim()
  if (osPlan && osPlan !== '—') lineas.push(`Financiador / plan: ${osPlan}`)
  return lineas
}

export default {
  name: 'PortalTurnosListadoSeccion',
  components: { ConfirmDialog, PortalTurnoPacienteCard },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [Error, Object, String, null],
      default: null,
    },
    /** home: grilla como resumen; full: lista en desktop para Mis turnos */
    variant: {
      type: String,
      default: 'full',
      validator: (v) => v === 'home' || v === 'full',
    },
    emptyMessage: {
      type: String,
      default: 'No hay turnos para mostrar.',
    },
    /** Solo turnos pendientes: muestra botón y diálogo para cancelar vía API */
    permiteCancelar: {
      type: Boolean,
      default: false,
    },
    noConfirmarAutomaticamenteTurnos: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      preparacionDialogCloseIcon: icons.CLOSE,
      cancelDialogAbierto: false,
      cancelEnviando: false,
      turnoIdCancelar: null,
      turnoCancelarItem: null,
      preparacionDialogAbierto: false,
      preparacionTurnoId: null,
      preparacionTurnoItem: null,
      preparacionTexto: '',
      preparacionEsHtml: false,
    }
  },
  computed: {
    emptyCopy() {
      return this.variant === 'home'
        ? 'No hay próximos turnos disponibles'
        : this.emptyMessage
    },
    colMd() {
      return this.variant === 'home' ? 6 : 12
    },
    skeletonColCount() {
      if (this.variant !== 'home') return 4
      return this.$vuetify.display.smAndUp ? 4 : 3
    },
    cardDisplay() {
      return this.variant === 'full' ? 'list' : 'card'
    },
    destacarPrimerTurno() {
      return this.variant === 'home' && this.items.length > 0
    },
    mensajeCancelarTurno() {
      const it = this.turnoCancelarItem
      const intro =
        '¿Confirma que desea cancelar el siguiente turno? Esta acción no se puede deshacer.'
      if (!it) return intro
      const lineas = lineasResumenTurnoPaciente(it)
      if (lineas.length === 0) return intro
      return `${intro}\n\n${lineas.join('\n')}`
    },
    resumenTurnoPreparacion() {
      const lineas = lineasResumenTurnoPaciente(this.preparacionTurnoItem)
      return lineas.length ? lineas.join('\n') : ''
    },
  },
  methods: {
    ...mapActions({
      cancelarTurnoPendiente: 'portalHome/cancelarTurnoPendiente',
      fetchTurnosPendientes: 'portalHome/fetchTurnosPendientes',
      fetchPreparacionTurnoPendiente: 'portalHome/fetchPreparacionTurnoPendiente',
    }),
    async onPedidoVerPreparacion(item) {
      const id = item?.turnoId
      if (id == null || !Number.isFinite(Number(id))) return
      this.preparacionTurnoItem = item
      this.preparacionTurnoId = Number(id)
      this.preparacionTexto = ''
      this.preparacionEsHtml = false
      try {
        const data = await this.fetchPreparacionTurnoPendiente({
          turnoId: id,
          fichaId: item?.fichaId,
        })
        const prep = preparacionDesdeRespuestaApi(data)
        const contenido = String(prep?.contenido ?? '').trim()
        this.preparacionTexto = contenido || 'Este turno no tiene preparación registrada.'
        this.preparacionEsHtml = contenido !== '' && !!prep?.esHtml
        this.preparacionDialogAbierto = true
      } catch {
        this.preparacionTexto = 'No se pudo obtener la preparación del turno.'
        this.preparacionEsHtml = false
        this.preparacionDialogAbierto = true
      } finally {
        this.preparacionTurnoId = null
      }
    },
    onPedidoCancelar(item) {
      const id = item?.turnoId
      if (id == null || !Number.isFinite(Number(id))) return
      this.turnoIdCancelar = Number(id)
      this.turnoCancelarItem = item
      this.cancelDialogAbierto = true
    },
    onCerrarDialogoCancelar() {
      this.turnoIdCancelar = null
      this.turnoCancelarItem = null
    },
    async onConfirmCancelarTurno() {
      if (this.turnoIdCancelar == null) return
      this.cancelEnviando = true
      try {
        await this.cancelarTurnoPendiente({ turnoId: this.turnoIdCancelar })
        await this.fetchTurnosPendientes()
        this.cancelDialogAbierto = false
        this.turnoIdCancelar = null
        this.turnoCancelarItem = null
      } catch {
        /* mensaje vía interceptor axios */
      } finally {
        this.cancelEnviando = false
      }
    },
  },
}
</script>

<style scoped>
.portal-turnos-listado-seccion__card-skeleton {
  min-height: 168px;
}
.portal-turnos-listado-seccion__card-skeleton :deep(.v-skeleton-loader__bone) {
  height: 100%;
  min-height: 100px;
}
.portal-turnos-listado-seccion__preparacion-dialog-body {
  max-height: min(78vh, 640px);
  overflow-y: auto;
}
.portal-turnos-listado-seccion__preparacion-card {
  border-color: #eceff1 !important;
}
.portal-turnos-listado-seccion__preparacion-text {
  white-space: pre-line;
}
.portal-turnos-listado-seccion__preparacion-html :deep(p) {
  margin-bottom: 8px;
}
.portal-turnos-listado-seccion__preparacion-html :deep(p:last-child) {
  margin-bottom: 0;
}
.portal-turnos-listado-seccion__preparacion-html :deep(ul),
.portal-turnos-listado-seccion__preparacion-html :deep(ol) {
  padding-left: 20px;
}
.portal-turnos-listado-seccion__preparacion-html :deep(a) {
  color: rgb(var(--v-theme-primary));
}
</style>
